import styled, { css } from "styled-components/macro";
import { HashLink as Link } from "react-router-hash-link";

import { colors, space, transition, fontStyle, iconSize } from "shared/styles/constants";

import { IMenuItemType } from "shared/components/SideMenu/types";
import { Icon } from "shared/styles";

export interface MenuItemProps {
  isOpen?: boolean;
  isActive?: boolean;
  type?: IMenuItemType;
  isDragging?: boolean;
  subItemIndex?: number;
  accentColor?: string;
}

const getTypeColor = (type?: IMenuItemType) => {
  switch (type) {
    case IMenuItemType.action:
      return { background: colors.grey1, hover: colors.grey2 };
    case IMenuItemType.add:
      return { background: colors.grey1, color: colors.grey2 };
    case IMenuItemType.default:
    default:
      return { background: colors.black, color: colors.grey1 };
  }
};

export const StyledMenuItem = styled.li<MenuItemProps>`
  position: relative;
  overflow: hidden;

  display: flex;
  justify-content: space-between;

  ${fontStyle.medium}

  background-color: ${({ isDragging, type }) =>
    isDragging ? colors.extraBlack : getTypeColor(type).background};

  &:before {
    content: "";

    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;

    width: 3px;

    transition: background-color ${transition.default}, opacity ${transition.default};

    background-color: ${colors.grey3};
    opacity: ${({ subItemIndex }) => (subItemIndex === 2 ? 1 : 0)};
  }

  &:hover {
    &::before {
      background-color: ${colors.grey4};
      opacity: 1;
    }
  }

  ${({ isActive, accentColor }) =>
    isActive &&
    css`
      &::before {
        background-color: ${accentColor || colors.green} !important; // override hover style
        opacity: 1;
      }
    `}

  ${({ isOpen }) =>
    isOpen &&
    css`
      &::before {
        background-color: ${colors.white}; // override hover style
        opacity: 1;
      }
    `}

  ${({ subItemIndex }) =>
    subItemIndex &&
    css`
      padding-left: calc(${subItemIndex} * ${space.m} + ${space.xxs});

      a,
      button {
        color: ${colors.grey5};
        ${fontStyle.regular}
      }
    `}
`;

const MenuItemStyle = css`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;

  padding: ${space.s};

  text-decoration: none;
  outline: none;

  &:hover,
  &:focus {
    & > svg {
      color: ${colors.white};
    }
  }
`;

export const MenuItemLink = styled(Link)`
  ${MenuItemStyle}

  transition: transform ${transition.default};

  &:focus {
    transform: translateX(${space.xxs});
  }
`;

export const MenuItemButton = styled.button`
  ${MenuItemStyle}

  [data-label] {
    transition: transform ${transition.default};
  }

  &:focus {
    [data-label] {
      transform: translateX(${space.xxs});
    }
  }
`;

export const MenuItemInner = styled.span`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;

  width: 100%;

  > *:first-child {
    flex: 0 0 auto; // don't squueze icon
  }

  > * + * {
    margin-left: ${space.xs};
  }
`;

export const MenuItemTitle = styled.span`
  flex-shrink: 1 !important;
`;

export const MenuItemIcon = styled(Icon)`
  transition: transform ${transition.default};
`;

export const ActionIcon = styled.div`
  margin-left: auto !important; // stick right
  transition: transform ${transition.default};
`;

export const ExpandIcon = styled.span`
  margin-left: auto !important; // stick right

  height: ${iconSize.s};
  width: ${iconSize.s};
`;

export const OverflowMenu = styled.div`
  display: flex;
  align-items: center;

  margin: ${space.s} ${space.xxs};

  height: 1rem; // align icon with text
`;
